import type { ReactNode } from 'react';
import { useScan } from './ScanLayout';
import { PageHeader, Section, Banner, KV, Stat, StatGrid, CodeBlock } from '../components/Section';
import { ModuleRemediation } from '../components/Remediation';

export function DnsPage() {
  const scan = useScan();
  const m = scan.modules.dns;

  if (!m) return (
    <>
      <PageHeader title="DNS Security" subtitle="Module result not available." />
      <Section accent="amber">
        <Banner tone="warn" title="DNS analyzer didn't return a result">
          The domain didn't resolve, or the DoH endpoint timed out. Try re-running.
        </Banner>
      </Section>
    </>
  );

  return (
    <>
      <PageHeader
        title="DNS Security Analysis"
        subtitle="DNSSEC, CAA records, and DNS configuration assessment"
      />

      <Section title="DNS Security Overview" accent="blue">
        <StatGrid cols={4}>
          <Stat label="DNSSEC" value={m.dnssec.enabled ? 'ENABLED' : 'OFF'}
                tone={m.dnssec.enabled ? 'ok' : 'warn'} sub="DNS tamper protection" />
          <Stat label="CAA Records" value={m.caa_count}
                tone={m.caa_count > 0 ? 'info' : 'mute'} sub="Authorized CAs" />
          <Stat label="IP Addresses" value={m.ip_count} tone="info" sub="A/AAAA records" />
          <Stat label="Mail Servers" value={m.mx_count}
                tone={m.mx_count > 0 ? 'info' : 'mute'} sub="MX endpoints" />
        </StatGrid>
      </Section>

      <Section
        title="DNSSEC (DNS Security Extensions)"
        accent={m.dnssec.enabled ? 'green' : 'amber'}
        status={m.dnssec.enabled
          ? { label: 'SECURED', tone: 'ok' }
          : { label: 'NOT ENABLED', tone: 'warn' }}
      >
        {m.dnssec.enabled ? (
          <Banner tone="ok" title="DNSSEC is properly configured and active">
            Your domain is protected against DNS spoofing, cache poisoning, and other
            DNS-based attacks through cryptographic signatures.
          </Banner>
        ) : (
          <Banner tone="warn" title="DNSSEC is not enabled">
            Enable DNSSEC at your registrar/DNS provider to cryptographically protect
            your zone against tampering.
          </Banner>
        )}
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <KV k="DS Records" v={m.dnssec.ds_records} />
          <KV k="Validation Chain" v={m.dnssec.validation_chain} />
          <KV k="Validation Steps" v={m.dnssec.validation_steps} />
          <KV k="Algorithm" v={m.dnssec.algorithm ?? '—'} />
          <KV k="Key Tag" v={m.dnssec.key_tag ?? '—'} />
        </div>
      </Section>

      <Section title="DNS Records" accent="blue">
        <div className="grid gap-5 md:grid-cols-2">
          <RecordBlock label="MX — Mail Servers" count={m.records.mx.length}>
            {[...m.records.mx].sort((a, b) => a.preference - b.preference).map((x, i) => (
              <div key={i} className="flex items-baseline gap-2 font-mono text-sm">
                <span className="pill pill-info shrink-0">{x.preference}</span>
                <span className="break-all">{x.exchange}</span>
              </div>
            ))}
          </RecordBlock>
          <RecordBlock label="NS — Nameservers" count={m.records.ns.length}>
            {m.records.ns.map((n, i) => (
              <div key={i} className="font-mono text-sm break-all">{n}</div>
            ))}
          </RecordBlock>
          <RecordBlock label="A — IPv4" count={m.records.a.length}>
            {m.records.a.map((ip, i) => <div key={i} className="font-mono text-sm">{ip}</div>)}
          </RecordBlock>
          <RecordBlock label="AAAA — IPv6" count={m.records.aaaa.length}>
            {m.records.aaaa.map((ip, i) => (
              <div key={i} className="font-mono text-sm break-all">{ip}</div>
            ))}
          </RecordBlock>
        </div>

        {m.records.soa && (
          <div className="mt-5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-ink-3">SOA — Start of Authority</p>
            <div className="mt-2 grid gap-3 sm:grid-cols-3">
              <KV k="Primary NS" v={m.records.soa.mname} />
              <KV k="Admin" v={rnameToEmail(m.records.soa.rname)} />
              <KV k="Serial" v={m.records.soa.serial} />
            </div>
          </div>
        )}

        {m.records.txt.length > 0 && (
          <div className="mt-5 space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-wider text-ink-3">TXT Records ({m.records.txt.length})</p>
            {m.records.txt.map((t, i) => <CodeBlock key={i}>{t}</CodeBlock>)}
          </div>
        )}
        {m.records.caa.length > 0 && (
          <div className="mt-5 space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-wider text-ink-3">CAA Records ({m.records.caa.length})</p>
            {m.records.caa.map((c, i) => (
              <CodeBlock key={i}>{`${c.flags} ${c.tag} "${c.value}"`}</CodeBlock>
            ))}
          </div>
        )}
      </Section>

      {scan.remediations.some(r => r.module === 'dns') && (
        <Section title="How to fix" accent="blue">
          <ModuleRemediation remediations={scan.remediations} modules={['dns']} />
        </Section>
      )}
    </>
  );
}

// A labelled group of DNS records with a count badge, or an em-dash when empty.
function RecordBlock({ label, count, children }: {
  label: string; count: number; children: ReactNode;
}) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-ink-3">
        {label} <span className="text-ink-3">({count})</span>
      </p>
      <div className="mt-1.5 space-y-1">
        {count > 0 ? children : <span className="text-sm text-ink-3">—</span>}
      </div>
    </div>
  );
}

// SOA RNAME encodes the zone admin's email with the first (unescaped) dot as '@'.
function rnameToEmail(rname: string): string {
  return rname.replace(/(?<!\\)\./, '@').replace(/\\\./g, '.');
}
